/**
 * Auditor Comments drawer — data hook and helpers.
 *
 * Loads auditor profiles + comments for the table, merges by financial year, and exposes MCA polling for the header.
 *
 * **Why `fetchAuditorComments` lives outside `useEffect`**
 * - Keeps the effect small: it only starts the request and registers cleanup (`cancelled` / `isCancelled`).
 * - The loader is easier to test and read than an inline async function.
 *
 * **What “cancellation” means here**
 * - This is **not** aborting the HTTP request on the wire (we do not pass `AbortSignal` to axios). The network call
 *   may still complete in the background.
 * - **Cancellation = this effect run is obsolete.** We set a flag (`cancelled = true`) in the effect **cleanup** when:
 *   - the user navigates away or the **component unmounts**, or
 *   - **`orgId` changes** so a **new** fetch is started for a different company.
 * - **`isCancelled()`** tells the **previous** in-flight `fetchAuditorComments` that its result must be **ignored**:
 *   we do not call `setRows` / `setLastUpdated` / `setLoading(false)` for that response, so we avoid:
 *   - **Stale UI:** showing org A’s auditor data after the user switched to org B.
 *   - **React warnings:** updating state on an unmounted component.
 * - So “cancelled” means **stop applying that request’s outcome to React state** — a common pattern for async `useEffect` work.
 *
 * **Why `void fetchAuditorComments(...)` in the effect**
 * - The effect callback must not be `async` (React expects a sync function or a cleanup function; `async` effects
 *   return a Promise, not an unsubscribe, and are easy to get wrong).
 * - We therefore call an async function without `await` — a deliberate “fire and forget” start of the request.
 * - Prefixing with `void` means: “we are intentionally ignoring the returned Promise.” That documents intent for
 *   readers and satisfies linters like `@typescript-eslint/no-floating-promises` / `no-misused-promises`.
 * - Completion is still handled inside `fetchAuditorComments` (state updates + `finally`); cancellation uses `isCancelled`.
 *
 * **`Dispatch` / `SetStateAction`**
 * - Not removed: they type the React state setters on `FetchAuditorCommentsArgs` in `auditorCommentsDrawer.types.ts`.
 */

import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { fetchAuditorCommentsData } from '../../../../../store/dashboard/auditedFinancials/service';
import { JAVA_API_STATUS } from '../../../../../utils/axios-interceptor';
import { DATA_FETCH_STATUS } from '../../../../../store/dashboard/constants';
import { useMcaPolling } from '../../../../../utils/customHooks';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../utils/dateUtils';
import type {
    AuditorComment,
    AuditorCommentEntry,
    AuditorProfile,
    AuditorReportCommentEntry,
    AuditorYearRow,
    CommentTextField,
    ExtractedAuditorCommentColumns,
    FetchAuditorCommentsArgs,
} from './auditorCommentsDrawer.types';
import { EMPTY_COMMENT_COLUMNS } from './constants';

export function formatYear(financialEndDate: string): string {
    if (!financialEndDate) return '';
    // financialEndDate comes as "2024-03-31" — FY ending March 2024 = FY’23-24
    const endYear = new Date(financialEndDate).getFullYear();
    const startYear = endYear - 1;
    return `FY’${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
}

function deriveAdverseRemarks(unfavourableRemarks: string | null): boolean | null {
    if (!unfavourableRemarks) return null;
    const lower = unfavourableRemarks.toLowerCase().trim();
    if (lower === 'yes' || lower === 'true' || lower === '1') return true;
    if (lower === 'no' || lower === 'false' || lower === '0') return false;
    return null;
}

function extractComments(
    directorReportEntries: AuditorCommentEntry[] | null,
    auditorReportEntries: AuditorReportCommentEntry[] | null | undefined,
): ExtractedAuditorCommentColumns {
    return {
        auditorComments: [...new Set([...pickTexts(auditorReportEntries, 'auditor'), ...pickTexts(directorReportEntries, 'auditor')])],
        directorComments: pickTexts(directorReportEntries, 'director'),
        auditorFootnotes: pickTexts(auditorReportEntries, 'footnote'),
        directorFootnotes: pickTexts(directorReportEntries, 'footnote'),
    };
}

function mergeData(profiles: AuditorProfile[], comments: AuditorComment[]): AuditorYearRow[] {
    const yearMap = new Map<string, AuditorYearRow>();

    for (const profile of profiles) {
        const key = profile.financialEndDate || '';
        yearMap.set(key, {
            year: key,
            ...toProfileFields(profile),
            reportHasAdverseRemarks: null,
            ...EMPTY_COMMENT_COLUMNS,
        });
    }

    for (const comment of comments) {
        const key = comment.financialEndDate || '';
        const extracted = extractComments(comment.auditorComment, comment.auditorReportComment);
        const remarks = deriveAdverseRemarks(comment.unfavourableRemarks);
        const hasData = remarks !== null || Object.values(extracted).some((texts) => texts.length > 0);
        const existing = yearMap.get(key);
        if (existing) {
            if (!hasData && existing.reportHasAdverseRemarks !== null) continue;
            existing.reportHasAdverseRemarks = remarks ?? existing.reportHasAdverseRemarks;
            for (const column of Object.keys(EMPTY_COMMENT_COLUMNS) as (keyof ExtractedAuditorCommentColumns)[]) {
                existing[column] = [...new Set([...existing[column], ...extracted[column]])];
            }
        } else {
            yearMap.set(key, {
                year: key,
                ...toProfileFields(null),
                reportHasAdverseRemarks: remarks,
                ...extracted,
            });
        }
    }

    return Array.from(yearMap.values()).sort((a, b) => b.year.localeCompare(a.year));
}

async function fetchAuditorComments({
    orgId,
    nature,
    isCancelled,
    setLoading,
    setRows,
    setLastUpdated,
    setAuditedTypeFromYears,
    setFetchFailed,
}: FetchAuditorCommentsArgs): Promise<void> {
    try {
        setLoading(true);
        const response = await fetchAuditorCommentsData(orgId, nature);
        if (isCancelled()) return;
        const responseData = response?.data?.responseData;
        const isSuccess = responseData?.responseCode === JAVA_API_STATUS.SUCCESS;
        setFetchFailed(!isSuccess);
        if (isSuccess) {
            const body = response?.data?.responseBody || {};
            const profiles: AuditorProfile[] = body.auditorProfiles || [];
            const comments: AuditorComment[] = body.auditorComments || [];

            setAuditedTypeFromYears(body.latestConsolidatedFY ?? null, body.latestStandaloneFY ?? null);
            setRows(mergeData(profiles, comments));

            const allDates = [...profiles, ...comments].map((item) => item.updatedAt).filter(Boolean) as string[];
            if (allDates.length) {
                const latest = allDates.sort().pop();
                setLastUpdated(latest ? dayjs(latest).format('DD MMM YYYY') : null);
            } else {
                setLastUpdated(null);
            }
        } else {
            setRows([]);
            setLastUpdated(null);
            setAuditedTypeFromYears(null, null);
        }
    } catch {
        if (!isCancelled()) {
            setFetchFailed(true);
            setRows([]);
            setLastUpdated(null);
            setAuditedTypeFromYears(null, null);
        }
    } finally {
        if (!isCancelled()) setLoading(false);
    }
}

/**
 * Hook: fetch and normalize auditor comments for `orgId`, plus MCA polling flag for the drawer header.
 *
 * @param orgId - company org ID
 * @param nature - 'CONSOLIDATED' | 'STANDALONE' | undefined. Pass undefined on first load (omits query param);
 *   pass the uppercase nature only after the user explicitly changes the Consolidated/Standalone control.
 * @param setAuditedTypeFromYears - from useAuditedType; called with API-returned FY values so that
 *   hook owns consolidation state and drives the dropdown. Replaces the old boolean flags that
 *   this hook previously managed and returned.
 */
export function useAuditorCommentsDrawer(
    orgId: string,
    nature: string | undefined,
    setAuditedTypeFromYears: (consolidatedFY: number | null, standaloneFY: number | null) => void,
) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<AuditorYearRow[]>([]);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);
    const [fetchFailed, setFetchFailed] = useState(false);
    const { mcaDocData } = useMcaPolling(orgId);
    const isMcaCalled = mcaDocData?.isFetching === true && mcaDocData?.reportStatus === DATA_FETCH_STATUS.PROCESSING;

    useEffect(() => {
        // Logical cancellation: see file doc ("What cancellation means here").
        // Cleanup sets cancelled=true when orgId/nature changes or on unmount.
        let cancelled = false;
        const isCancelled = () => cancelled;

        // See file doc: `void` = intentional fire-and-forget; effect cannot be async.
        void fetchAuditorComments({
            orgId,
            nature,
            isCancelled,
            setLoading,
            setRows,
            setLastUpdated,
            setAuditedTypeFromYears,
            setFetchFailed,
        });

        return () => {
            cancelled = true;
        };
    }, [orgId, nature]);

    const mcaLastUpdated = formatDateByFormat(mcaDocData?.updatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY) || null;

    return { loading, rows, lastUpdated: lastUpdated ?? mcaLastUpdated, isMcaCalled, fetchFailed };
}

function pickTexts(entries: Partial<Record<CommentTextField, string>>[] | null | undefined, field: CommentTextField): string[] {
    if (!Array.isArray(entries)) return [];
    return entries.map((entry) => entry?.[field]).filter((text): text is string => !!text);
}

function toProfileFields(profile: AuditorProfile | null) {
    return {
        auditorName: profile?.nameOfAuditor || null,
        auditorFirmName: profile?.firmName || null,
        auditorMembershipNum: profile?.membershipNum || null,
        auditorFirmRegistrationNumber: profile?.firmRegistrationNumber || null,
        auditorAddress: profile?.address || null,
        auditorPan: profile?.pan || null,
        additionalAuditors: profile?.additionalAuditors ?? [],
    };
}
